import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, makeStyles, Tooltip, useTheme } from '@material-ui/core';
import { Rotate90DegreesCcw } from '@material-ui/icons';
import { ResponsiveBar } from '@nivo/bar';
import { NoDataMessage } from '../../NoDataMessage';
import { CustomLegend } from './CustomLegend';

export const Bar = (props) => {
  const { data, bottomLabel, leftLabel, theme } = props;
  const classes = useStyles();
  const [rotate, setRotate] = useState(false);
  const [hideItems, setHideItems] = useState([]);
  const curTheme = useTheme();

  useEffect(() => {
    setFilteredData(displayData);
  }, [data]);

  const total = data.map((v) => v.value).reduce((prev, cur) => prev + cur, 0);

  const displayData = data.map((cur) => ({
    id: cur.id,
    [cur.id]: cur.value,
  }));
  const [filteredData, setFilteredData] = useState(displayData);
  const keys = data.map((k) => k.id);

  const setRotation = () => {
    rotate ? setRotate(false) : setRotate(true);
  };

  const handleClick = (key) => {
    let curItems = hideItems;

    curItems.includes(key)
      ? curItems.splice(curItems.indexOf(key), 1)
      : curItems.push(key);

    setHideItems(curItems);
    setFilteredData(displayData.filter((d) => !hideItems.includes(d.id)));
  };

  const gridTheme = {
    axis: {
      legend: {
        text: {
          fill: curTheme.palette.typeColor.graph,
        },
      },
      grid: {
        line: {
          stroke: curTheme.palette.typeColor.graph,
        },
      },
    },
  };

  const customTick = (tick, axis) => {
    const x = axis === 'x' ? 0 : 15;
    const y = axis === 'y' ? 0 : 15;
    const formatTick =
      tick.value.length > 4 ? (
        <tspan>
          {tick.value.substring(0, 4) + '...'}
          <title>{tick.value}</title>
        </tspan>
      ) : (
        tick.value
      );
    return (
      <g transform={`translate(${tick.x - x},${tick.y + y})`}>
        <text
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fill: curTheme.palette.typeColor.graph,
            fontSize: 10,
          }}
        >
          {formatTick}
        </text>
      </g>
    );
  };

  const leftAxisElement = () => ({
    tickSize: 5,
    tickPadding: 5,
    tickRotation: `${!rotate ? 0 : -45}`,
    legend: `${!rotate ? leftLabel : bottomLabel}`,
    legendPosition: 'middle',
    legendOffset: -50,
    renderTick: (tick) => customTick(tick, 'y'),
  });

  return (
    <div className={classes.graphContainer}>
      {total > 0 ? (
        <>
          <div className={classes.buttonContainer}>
            <IconButton
              className={classes.button}
              size="small"
              onClick={setRotation}
            >
              <Tooltip title={`View ${!rotate ? 'Horizontal' : 'Vertical'}`}>
                <Rotate90DegreesCcw className={classes.button} />
              </Tooltip>
            </IconButton>
            <CustomLegend keys={keys} colors={theme} onHide={handleClick} />
          </div>
          <ResponsiveBar
            data={filteredData}
            keys={keys}
            indexBy="id"
            layout={rotate ? 'horizontal' : 'vertical'}
            margin={{
              top: 25,
              right: 20,
              bottom: 50,
              left: `${!rotate ? 60 : 20}`,
            }}
            padding={0.3}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            colors={theme}
            borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            axisTop={null}
            axisRight={null}
            enableGridX={rotate}
            enableGridY={!rotate}
            theme={gridTheme}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: `${!rotate ? bottomLabel : leftLabel}`,
              legendPosition: 'middle',
              legendOffset: 35,
              renderTick: (tick) => customTick(tick, 'x'),
            }}
            axisLeft={!rotate ? leftAxisElement() : null}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            tooltip={({ id, value, color }) => (
              <div
                style={{
                  fontSize: 12,
                  padding: 12,
                  background: '#222222',
                  zIndex: 20,
                }}
              >
                <strong style={{ color }}>{id}:</strong>{' '}
                <span style={{ color: 'white' }}>{value}</span>
              </div>
            )}
          />
        </>
      ) : (
        <NoDataMessage subTitle="Try Again Later" />
      )}
    </div>
  );
};

Bar.propTypes = {
  data: PropTypes.array.isRequired,
  bottomLabel: PropTypes.string.isRequired,
  leftLabel: PropTypes.string.isRequired,
  theme: PropTypes.array,
};

const useStyles = makeStyles({
  graphContainer: {
    width: '100%',
    height: '100%',
  },
  button: {
    padding: 2,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  buttonContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'left',
    paddingRight: 15,
    marginTop: -18,
  },
});
